import { useCallback, useContext } from 'react';
import { useAtom } from 'jotai';
import { useChatClient } from '@bizzabo/chat-core';
import ChatMessageDialog from '../CustomMessage/ChatMessageDialog';
import ChatMessageDialogTitle from './components/ChatMessageDialogTitle';
import ChatMessageDialogDescription from './components/ChatMessageDialogDescription';
import ChatMessageDialogConfirmCancelFooter from './components/ChatMessageDialogConfirmCancelFooter';
import { banUser } from '../../utils/userUtils';
import SessionChatCtx from '../../context/SessionChatCtx';
import { userBanForFirstTimeAtom, userIdToBanMessageAtom } from '../../atoms/MessageActionsAtoms';
import { UserBanStatus } from '../../types/UserBanStatus';

const BanUserDialog = ({ sessionChatContainer, trackClickEvent }) => {
    const sessionChatCtx = useContext(SessionChatCtx);
    const { client } = useChatClient();
    const [userIdToBanMessage, setUserIdToBanMessage] = useAtom(userIdToBanMessageAtom);
    const [, setUserBanForFirstTime] = useAtom(userBanForFirstTimeAtom);

    const onClose = useCallback(async () => {
        const userToBanMessage = (await client.getMessage(userIdToBanMessage)).message;
        await trackClickEvent({
            buttonType: 'BAN_MESSAGE_POP_UP',
            subCategory: 'CANCEL',
            description: {
                message_type: 'TEXT',
                message_id: userToBanMessage.id,
                is_pinned: userToBanMessage.pinned,
                parent_type: userToBanMessage?.parent_id && 'TEXT',
                parent_id: userToBanMessage.parent_id,
                banned_user_id: userToBanMessage.user.id,
            },
        });
        setUserIdToBanMessage('');
    }, [setUserIdToBanMessage]);

    const onConfirm = useCallback(async () => {
        const userToBanMessage = (await client.getMessage(userIdToBanMessage)).message;
        await banUser(
            sessionChatCtx.accountId,
            sessionChatCtx.eventId,
            sessionChatCtx.sessionId,
            userToBanMessage.user.id
        );
        await trackClickEvent({
            buttonType: 'BAN_MESSAGE_POP_UP',
            subCategory: 'BLOCK',
            description: {
                message_type: 'TEXT',
                message_id: userToBanMessage.id,
                is_pinned: userToBanMessage.pinned,
                parent_type: userToBanMessage?.parent_id && 'TEXT',
                parent_id: userToBanMessage.parent_id,
                banned_user_id: userToBanMessage.user.id,
            },
        });
        setUserIdToBanMessage('');
        setUserBanForFirstTime(UserBanStatus.FirstTimeBan);
    }, [setUserIdToBanMessage]);

    return (
        <ChatMessageDialog parentElementRef={sessionChatContainer} onClose={onClose}>
            <ChatMessageDialogTitle>
                You are about to block this attendee from all communication
            </ChatMessageDialogTitle>
            <ChatMessageDialogDescription>
                When blocked, the attendee will no longer have access to session chats or direct
                messaging and will be notified of this. You can later choose to unblock this
                attendee. Are you sure you want to proceed?
            </ChatMessageDialogDescription>
            <ChatMessageDialogConfirmCancelFooter
                confirmText="Yes, Block Attendee"
                onClose={onClose}
                onConfirm={onConfirm}
            />
        </ChatMessageDialog>
    );
};

export default BanUserDialog;
